import React, { useState, useEffect, useMemo, useCallback } from "react";
import { API } from "aws-amplify";
import Fuse from "fuse.js";

import SearchAddButton from "../../SearchAddButton";
import { sortArrayOfObjects } from "../../../js/Helpers";
import { getLabelColorClass } from "../utils/labelColorUtils";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faSearch, faSquare } from "@fortawesome/free-solid-svg-icons";
import { faSquare as farSquare } from "@fortawesome/free-regular-svg-icons";

export default function StudentLabelAssignments({ schoolInfo, students, setStudents, labelInfo }) {
	const label_id = `${labelInfo?.label_id || ''}`;
	const label_name = labelInfo?.label || "(No Name)";
	const students_data = Array.isArray(students) ? students : [];

	const [gradeLevels, setGradeLevels] = useState([]);
	const [gradesSelected, setGradesSelected] = useState([]);
	const [studentsByGradeSelected, setStudentsByGradeSelected] = useState([]);
	const [selectedStudents, setSelectedStudents] = useState([]);

	const [filterIDs, setFilterIDs] = useState([]);
	const [filterExistingStudentIDs, setFilterExistingStudentIDs] = useState([]);

	const studentHasLabel = useCallback((student_info) => {
		const student_labels = Array.isArray(student_info?.student_labels) ? student_info.student_labels : [];
		return student_labels.findIndex((student_label) => `${student_label?.label_id}` === label_id) !== -1;
	}, [label_id]);

	const sorted_students_with_label = useMemo(() => {
		const students_with_label = students_data.filter((student_info) => studentHasLabel(student_info));
		return sortArrayOfObjects(students_with_label, "last_name", "text", "asc");
	}, [students_data, studentHasLabel]);

	const fuzzySearch = (e, search_type) => {
		const search_value = `${e.target.value || ""}`;
		if(search_value.trim().length === 0)
		{
			const student_ids = students_data.map((student_info) => student_info.student_id);
			if(search_type === "existing") setFilterExistingStudentIDs(student_ids);
			if(search_type === "all") setFilterIDs(student_ids);
			return;
		}

		const students_fuse = new Fuse(students_data, {
			keys: ["last_name", "first_name"],
			threshold: 0.1,
		});

		const student_results = students_fuse.search(search_value);
		const subset_student_results = student_results.map((result) => result.item).slice(0, 100);
		const filtered_student_ids = subset_student_results.map((student_info) => student_info.student_id);

		if(search_type === "existing") setFilterExistingStudentIDs([...filtered_student_ids]);
		if(search_type === "all") setFilterIDs([...filtered_student_ids]);
	};

	const toggleSelectStudent = (student_id) => {
		const student_id_key = `${student_id}`;
		const student_selected_index = selectedStudents.findIndex((selected_id) => `${selected_id}` === student_id_key);

		if(student_selected_index !== -1) selectedStudents.splice(student_selected_index, 1);
		else selectedStudents.push(student_id_key);

		setSelectedStudents([...selectedStudents]);
	};

	const toggleGradeSelected = (grade) => {
		const grade_currently_selected_index = gradesSelected.findIndex((selected_grade) => `${selected_grade}` === `${grade}`);
		let new_students_by_grade_selected = [];

		if(grade_currently_selected_index !== -1)
		{
			new_students_by_grade_selected = studentsByGradeSelected.reduce((results, student_id) => {
				const student_info = students_data.find((student) => `${student.student_id}` === `${student_id}`);
				const student_grade = student_info ? student_info.grade : null;

				if(`${student_grade}` !== `${grade}`) results.push(student_id);
				return results;
			}, []);

			gradesSelected.splice(grade_currently_selected_index, 1);
		}
		else
		{
			new_students_by_grade_selected = students_data.reduce((results, student_info) => {
				const student_id = student_info.student_id;
				const student_grade = student_info.grade;
				const student_has_label = studentHasLabel(student_info);

				if(!studentsByGradeSelected.includes(student_id) && !student_has_label && `${student_grade}` === `${grade}`) results.push(student_id);
				return results;
			}, studentsByGradeSelected);

			gradesSelected.push(grade);
		}

		setStudentsByGradeSelected([...new_students_by_grade_selected]);
		setGradesSelected([...gradesSelected]);
	};

	const updateDatabase = async (update_type, student_ids) => {
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-student-label';

		await Promise.all(student_ids.map((student_id) => {
			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:update_type, student_id:student_id, label_id:label_id};
			const myInit = {
				response: true,
				body: JSON.stringify(data),
			};

			return API.post(apiName, url, myInit);
		}));
	};

	const updateStudentsLocal = (update_type, student_ids) => {
		const student_id_set = new Set(student_ids.map((student_id) => `${student_id}`));

		setStudents((previous_students) => {
			const previous_students_array = Array.isArray(previous_students) ? previous_students : [];

			return previous_students_array.map((student_info) => {
				const student_id_key = `${student_info.student_id}`;
				if(!student_id_set.has(student_id_key)) return student_info;

				const student_labels = Array.isArray(student_info.student_labels) ? [...student_info.student_labels] : [];
				const label_index = student_labels.findIndex((student_label) => `${student_label?.label_id}` === label_id);

				if(update_type === "add" && label_index === -1) student_labels.push({label_id: label_id});
				if(update_type === "remove" && label_index !== -1) student_labels.splice(label_index, 1);

				return {...student_info, student_labels: student_labels};
			});
		});
	};

	const addLabelToStudents = async (student_ids) => {
		const filtered_student_ids = student_ids.filter((student_id) => {
			const student_info = students_data.find((student) => `${student.student_id}` === `${student_id}`);
			return student_info ? !studentHasLabel(student_info) : false;
		});
		if(filtered_student_ids.length === 0) return;

		updateStudentsLocal("add", filtered_student_ids);
		setStudentsByGradeSelected([]);
		setGradesSelected([]);
		setSelectedStudents([]);

		try {
			await updateDatabase("add", filtered_student_ids);
		} catch(e)
		{
			console.log(e?.response || e);
		}
	};

	const removeLabelFromStudents = async (student_ids) => {
		if(student_ids.length === 0) return;

		updateStudentsLocal("remove", student_ids);
		setSelectedStudents([]);

		try {
			await updateDatabase("remove", student_ids);
		} catch(e)
		{
			console.log(e?.response || e);
		}
	};

	useEffect(() => {
		const grades = students_data.reduce((results, student_info) => {
			const student_grade = student_info.grade;
			if(results.findIndex((grade) => `${grade}` === `${student_grade}`) === -1) results.push(student_grade);
			return results;
		}, []);

		grades.sort((a, b) => Number(a) - Number(b));
		setGradeLevels(grades);
	}, [students_data]);

	useEffect(() => {
		const student_ids = students_data.map((student_info) => student_info.student_id);
		setFilterIDs([...student_ids]);
		setFilterExistingStudentIDs([...student_ids]);
	}, [students_data]);

	return (
		<>
			<h4>Student Labels</h4>
			<h1 className='report-name'>
				<div className='create-labels-section-label' style={{margin:'0px', width:'fit-content'}}>
					<div className={`create-labels-icon ${getLabelColorClass(labelInfo)}`}></div>
					<div>{label_name}</div>
				</div>
			</h1>
			<div className='student-requests-course-container'>
				<div className='student-requests-add-request-container'>
					<div className='student-requests-subheader-container'>
						<h2 style={{ margin: "0px" }}>Add Label To Students</h2>
					</div>
					<h3>By student:</h3>
					<div className='search-add-adding-input-container'>
						<input className='search-add-adding-input' onChange={(e) => fuzzySearch(e, "all")} placeholder='Search for student...' />
						<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch} autoFocus={true}/>
					</div>
					<div className='search-add-search-results' style={{ maxHeight: "400px" }}>
						{students_data.map((student_info, i) => {
							const student_id = student_info.student_id;
							if(!filterIDs.includes(student_id)) return null;

							const student_has_label = studentHasLabel(student_info);
							return (
								<div className='search-add-search-result student-requests-report-search-result' key={i}>
									<div>
										<div>{student_info.last_name}, {student_info.first_name}</div>
										<div className='small-text dark-gray-text'>{student_info.grade}th grade</div>
									</div>
									<SearchAddButton selected={student_has_label} onAdd={() => addLabelToStudents([student_id])} label='Add Label' />
								</div>
							);
						})}
					</div>
					<h3>By grade:</h3>
					<div>
						{gradeLevels.map((grade) => {
							const grade_selected = gradesSelected.includes(grade);

							return (
								<div className='student-requests-grade-row' key={grade} onClick={() => toggleGradeSelected(grade)}>
									<FontAwesomeIcon icon={grade_selected ? faCheckSquare : faSquare} className={grade_selected ? "fas-checkbox-checked" : "fas-checkbox-unchecked"} />
									<div>{grade}</div>
								</div>
							);
						})}
						{studentsByGradeSelected.length > 0 &&
							<>
								<div className='student-requests-add-by-grade-student-list'>
										{studentsByGradeSelected.map((student_id, j) => {
											const student_info = students_data.find((student) => `${student.student_id}` === `${student_id}`);
										if(!student_info) return null;

										if(filterIDs.length > 0 && !filterIDs.includes(student_id)) return null;

										return (
											<div className='student-requests-request-line' key={j}>
												<div>
													<div>{student_info.last_name}, {student_info.first_name}</div>
													<div className='small-text dark-gray-text'>{student_info.grade}th grade</div>
												</div>
											</div>
										);
									})}
								</div>
								<div className='btn blue-btn' onClick={() => addLabelToStudents(studentsByGradeSelected)}>Add Label To {studentsByGradeSelected.length} Students</div>
							</>
						}
					</div>
				</div>
				<div className='student-requests-current-requests-container'>
					<div className='student-requests-subheader-container'>
						<h2 style={{ margin: "0px" }}>Current Students With Label</h2>
						<div className='medium-text'>{sorted_students_with_label.length} students</div>
					</div>
					<div className='reports-search-bar-container'>
						<input className='reports-search-bar' placeholder='Search for a student...' defaultValue='' onChange={(e) => fuzzySearch(e, "existing")} />
						<FontAwesomeIcon className='reports-search-bar-icon' icon={faSearch}/>
					</div>
					<div style={{ marginTop: "20px" }}>
						{sorted_students_with_label.length === 0 ?
							(
								<div className='conflict-matrix-no-data'>No students have this label</div>
							):
							(
								<>
									{selectedStudents.length > 0 &&
										<div className='btn red-btn' style={{marginBottom:'10px'}} onClick={() => removeLabelFromStudents(selectedStudents)}>Remove Label From {selectedStudents.length} Students</div>
									}
									<div className='modal-example-list'>
										{sorted_students_with_label.map((student_info, j) => {
											const student_id = student_info.student_id;
											if(filterExistingStudentIDs.length > 0 && !filterExistingStudentIDs.includes(student_id)) return null;

											const student_selected = selectedStudents.includes(`${student_id}`);

											return (
												<div className='student-requests-request-line' key={j}>
													<FontAwesomeIcon className={student_selected ? "blue-text" : "dark-gray-text"} icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student_id)}/>
													<div>
														<div>{student_info.last_name}, {student_info.first_name}</div>
														<div className='small-text dark-gray-text'>{student_info.grade}th grade</div>
													</div>
												</div>
											);
										})}
									</div>
								</>
							)
						}
					</div>
				</div>
			</div>
		</>
	);
}
